import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

function fmtDate(dateStr) {
  if (!dateStr) return '';
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtHours(h) {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

// Podium visual order: 2nd on the left, 1st (tallest) in the middle, 3rd on the right.
const PODIUM_ORDER = [1, 0, 2];
const PODIUM_META = {
  0: { medal: '🥇', label: '1st', blockHeight: 92, badgeBg: '#fbbf24', badgeText: '#78350f' },
  1: { medal: '🥈', label: '2nd', blockHeight: 66, badgeBg: '#cbd5e1', badgeText: '#334155' },
  2: { medal: '🥉', label: '3rd', blockHeight: 48, badgeBg: '#f59e0b', badgeText: '#78350f' },
};
const PODIUM_AVATAR_SIZE = 68;

function PosterAvatar({ name, imagePath, size }) {
  const [imgError, setImgError] = useState(false);
  const hasImage = imagePath && !imgError;
  const src = hasImage
    ? getImageURL(imagePath)
    : `https://placehold.co/256x256?text=${encodeURIComponent(name || 'Staff')}`;
  return (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      style={{
        width: size, height: size, borderRadius: '9999px', objectFit: 'cover',
        border: '4px solid #fff', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', background: '#e5e7eb',
        flexShrink: 0,
      }}
    />
  );
}

function Stars({ value }) {
  const rounded = Math.round(value || 0);
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} viewBox="0 0 20 20" width="14" height="14" fill={i <= rounded ? '#fbbf24' : 'rgba(255,255,255,0.35)'}>
          <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L10 14.9l-5.2 2.62.99-5.8-4.21-4.1 5.82-.85L10 1.5z" />
        </svg>
      ))}
    </span>
  );
}

const SQUARE_SIZE = 480;

// One self-contained, square (1:1) podium banner per leaderboard.
function PodiumSquare({ gradient, icon, title, subtitle, entries, renderMetric, tagline }) {
  return (
    <div
      style={{
        width: SQUARE_SIZE, height: SQUARE_SIZE, overflow: 'hidden',
        background: gradient, padding: '32px 28px', color: '#fff',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 34, lineHeight: 1 }}>{icon}</div>
        <h1 style={{ fontSize: 21, fontWeight: 800, margin: '8px 0 4px', letterSpacing: 0.2 }}>{title}</h1>
        <p style={{ fontSize: 12, opacity: 0.9, margin: 0 }}>{subtitle}</p>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 12, marginTop: 12 }}>
        {entries.length === 0 ? (
          <p style={{ fontSize: 13, opacity: 0.85, fontStyle: 'italic' }}>No data for this period.</p>
        ) : (
          PODIUM_ORDER.map((posIdx) => {
            const entry = entries[posIdx];
            if (!entry) return <div key={posIdx} style={{ width: 130 }} />;
            const meta = PODIUM_META[posIdx];
            // Avatar/name/metric sizing is IDENTICAL across ranks -- only the medal
            // and the base block height vary, so the 3 columns' content lines up
            // evenly and only the podium step itself rises for 1st place.
            return (
              <div key={entry.staffId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 130 }}>
                <div style={{ fontSize: 24, marginBottom: 10, lineHeight: 1 }}>{meta.medal}</div>
                <PosterAvatar name={entry.staffName} imagePath={entry.imagePath} size={PODIUM_AVATAR_SIZE} />
                <p style={{
                  boxSizing: 'border-box', fontSize: 13, fontWeight: 700, margin: '8px 0 4px', textAlign: 'center', lineHeight: '16px',
                  width: 120, height: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {entry.staffName}
                </p>
                {renderMetric(entry)}
                <div
                  style={{
                    boxSizing: 'border-box', width: '100%', height: meta.blockHeight, marginTop: 10,
                    background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
                    borderBottom: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <span style={{
                    boxSizing: 'border-box', display: 'inline-block', fontSize: 12, lineHeight: '16px', fontWeight: 800,
                    color: meta.badgeText, background: meta.badgeBg, padding: '3px 12px',
                  }}>
                    {meta.label}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p style={{ textAlign: 'center', fontSize: 11, opacity: 0.85, margin: 0 }}>{tagline}</p>
    </div>
  );
}

export default function LecturerRatingsPoster() {
  const [lecturers, setLecturers] = useState([]);
  const [teachingHours, setTeachingHours] = useState([]);
  const [correctedAssignments, setCorrectedAssignments] = useState([]);
  const [feedbackHours, setFeedbackHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const rangeLabel = `${fmtDate(startDate)} — ${fmtDate(endDate)}`;

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [ratingsResp, leaderboardsResp] = await Promise.all([
          axiosInstance.get('/admin/dashboard/lecturer-ratings', { params: { startDate, endDate } }),
          axiosInstance.get('/admin/dashboard/lecturer-leaderboards', { params: { startDate, endDate } }),
        ]);
        if (!isMounted) return;
        setLecturers((ratingsResp?.data?.lecturers || []).slice(0, 3));
        setTeachingHours(leaderboardsResp?.data?.teachingHours || []);
        setCorrectedAssignments(leaderboardsResp?.data?.correctedAssignments || []);
        setFeedbackHours(leaderboardsResp?.data?.feedbackHours || []);
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || 'Failed to load poster data');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [startDate, endDate]);

  const hasAnyData = lecturers.length > 0 || teachingHours.length > 0 || correctedAssignments.length > 0 || feedbackHours.length > 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: '24px 16px' }}>
      <div className="print-hide" style={{ maxWidth: 1024, margin: '0 auto 20px' }}>
        <button
          type="button"
          onClick={() => window.close()}
          style={{ fontSize: 12, fontWeight: 600, color: '#475569', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
        >
          ← Close
        </button>
      </div>

      {error && (
        <div style={{ maxWidth: 1024, margin: '0 auto 16px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: 14 }}>Loading poster data…</p>
      ) : !hasAnyData ? (
        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, fontStyle: 'italic' }}>
          No data found for this date range — nothing to put on a poster yet.
        </p>
      ) : (
        <div style={{
          maxWidth: 1024, margin: '0 auto', display: 'flex', flexWrap: 'wrap',
          justifyContent: 'center', gap: 32,
        }}>
          <PodiumSquare
            gradient="linear-gradient(160deg, #7c2d12 0%, #b45309 45%, #d97706 100%)"
            icon="🏆"
            title="Favorite Lecturers"
            subtitle={`${rangeLabel} · By student rating`}
            entries={lecturers}
            renderMetric={(e) => (
              <>
                <Stars value={e.avgRating} />
                <p style={{ fontSize: 11, fontWeight: 700, margin: '3px 0 0', color: '#fef3c7' }}>
                  {e.avgRating !== null ? e.avgRating.toFixed(2) : '—'} / 5
                </p>
              </>
            )}
            tagline="Keep up the great teaching ⭐"
          />

          <PodiumSquare
            gradient="linear-gradient(160deg, #1e3a8a 0%, #1d4ed8 45%, #3b82f6 100%)"
            icon="📚"
            title="Teaching + Event Hours"
            subtitle={`${rangeLabel} · Combined hours`}
            entries={teachingHours}
            renderMetric={(e) => (
              <p style={{ fontSize: 13, fontWeight: 800, margin: '3px 0 0', color: '#dbeafe' }}>{fmtHours(e.combinedHours)}</p>
            )}
            tagline="Thank you for the time invested 📘"
          />

          <PodiumSquare
            gradient="linear-gradient(160deg, #14532d 0%, #15803d 45%, #22c55e 100%)"
            icon="✅"
            title="Assignments Corrected"
            subtitle={`${rangeLabel} · Total corrected`}
            entries={correctedAssignments}
            renderMetric={(e) => (
              <p style={{ fontSize: 13, fontWeight: 800, margin: '3px 0 0', color: '#dcfce7' }}>{e.totalCorrected}</p>
            )}
            tagline="Fast feedback keeps students moving ✅"
          />

          <PodiumSquare
            gradient="linear-gradient(160deg, #581c87 0%, #7e22ce 45%, #a855f7 100%)"
            icon="💬"
            title="Feedback Hours Given"
            subtitle={`${rangeLabel} · Hours with students`}
            entries={feedbackHours}
            renderMetric={(e) => (
              <p style={{ fontSize: 13, fontWeight: 800, margin: '3px 0 0', color: '#f3e8ff' }}>{fmtHours(e.totalHours)}</p>
            )}
            tagline="Every conversation helps a student grow 💬"
          />
        </div>
      )}
    </div>
  );
}
